import { Loader2, Send, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactMarkdown from 'react-markdown';
import { MAX_MESSAGE_LENGTH } from '../config/constants';
import useViewportHeight from '../hooks/useViewportHeight';
import { chatAPI } from '../services/api';
import Header from './Header';
import Sidebar from './Sidebar';
import SurveyQRCode from './SurveyQRCode';

const ChatInterface = ({ user, onSignOut }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle viewport height changes for mobile keyboard
  useViewportHeight();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Convert history format to message format
  // Note: We intentionally exclude tool calls and results from loaded history
  // to keep the conversation clean and focused on the actual dialogue
  const convertHistoryToMessages = (history) => {
    return history.map((item, index) => ({
      id: Date.now() + index,
      text: item.content,
      sender: item.role === 'ai' ? 'ai' : 'user',
      timestamp: item.timestamp
        ? new Date(item.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })
        : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      // Explicitly set these to empty arrays for loaded messages
      // This ensures QR codes and tool results don't appear when viewing history
      toolCalls: [],
      toolResults: [],
    }));
  };

  // Load an existing session
  const loadSession = async (selectedSessionId) => {
    try {
      setIsLoading(true);
      const { history } = await chatAPI.getSessionHistory(selectedSessionId);

      setSessionId(selectedSessionId);
      setMessages(convertHistoryToMessages(history));
      setError(null);
    } catch (err) {
      console.error('Failed to load session history:', err);
      setError('Failed to load conversation history');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new session
  const createNewSession = async () => {
    try {
      setIsLoading(true);
      const { sessionId: newSessionId, welcome_message } = await chatAPI.createSession(
        user.uid
      );

      setSessionId(newSessionId);

      // Add welcome message to chat if present
      if (welcome_message) {
        const welcomeMsg = {
          id: Date.now(),
          text: welcome_message,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        };
        setMessages([welcomeMsg]);
      } else {
        setMessages([]);
      }

      // Refresh sessions list
      const { sessions: fetchedSessions } = await chatAPI.getSessions();
      setSessions(fetchedSessions || []);

      setError(null);
    } catch (err) {
      console.error('Failed to create session:', err);

      // Check if it's an authentication error
      if (err.message.includes('authenticated') || err.message.includes('401')) {
        setError('Authentication failed. Please sign in again.');
        setTimeout(() => {
          if (onSignOut) onSignOut();
        }, 2000);
      } else {
        setError('Failed to initialize chat session');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle session selection from sidebar
  const handleSelectSession = async (selectedSessionId) => {
    await loadSession(selectedSessionId);
    setIsSidebarOpen(false); // Close sidebar on mobile
  };

  // Handle new chat button
  const handleNewChat = async () => {
    await createNewSession();
    setIsSidebarOpen(false); // Close sidebar on mobile
  };

  // Handle sidebar toggle
  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch sessions on mount
  useEffect(() => {
    let mounted = true;

    const fetchSessions = async () => {
      try {
        const { sessions: fetchedSessions } = await chatAPI.getSessions();
        if (mounted) {
          setSessions(fetchedSessions || []);

          // Always create a new session on page load
          await createNewSession();
        }
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        if (mounted) {
          // If fetching sessions fails, just create a new session
          await createNewSession();
        }
      }
    };

    fetchSessions();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  const preventBlur = (e) => {
    e.preventDefault();
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !sessionId) {
      console.log('Cannot send:', {
        hasMessage: !!inputMessage.trim(),
        isLoading,
        hasSession: !!sessionId,
      });
      return;
    }

    const userMessageText = inputMessage.trim();

    // Enforce character limit
    if (userMessageText.length > MAX_MESSAGE_LENGTH) {
      setError(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit`);
      return;
    }
    setInputMessage('');
    setError(null);

    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      text: userMessageText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Send to backend
      console.log('Sending message with sessionId:', sessionId);
      const response = await chatAPI.sendMessage(sessionId, userMessageText);
      console.log('Received response:', response);

      // Add AI response to UI with tool calls
      const aiMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        toolCalls: response.tool_calls || [],
        toolResults: response.tool_results || [],
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Refresh sessions list only if this was the first user message
      // (to update the session title in the sidebar)
      if (messages.length <= 1) {
        try {
          const { sessions: fetchedSessions } = await chatAPI.getSessions();
          setSessions(fetchedSessions || []);
        } catch (err) {
          console.error('Failed to refresh sessions:', err);
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);

      // Check if it's an authentication error
      if (err.message.includes('authenticated') || err.message.includes('401')) {
        setError('Authentication expired. Please sign in again.');
        setTimeout(() => {
          if (onSignOut) onSignOut();
        }, 2000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-screen-mobile bg-nova-bg flex flex-col">
      <Sidebar
        sessions={sessions}
        activeSessionId={sessionId}
        onSelectSession={handleSelectSession}
        onNewChat={handleNewChat}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <Header user={user} onSignOut={onSignOut} onToggleSidebar={handleToggleSidebar} />

      {/* Messages Area - Centered with max width */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-3xl mx-auto px-6 py-6 pb-6 space-y-4">
          {messages.length === 0 && !error && (
            <div className="text-center mt-20">
              <div className="w-16 h-16 bg-gradient-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Start a conversation</h3>
              <p className="text-gray-400">Send a message to begin chatting with NovaSage</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} min-w-0`}
            >
              <div
                className={`max-w-2xl min-w-0 ${message.sender === 'user'
                  ? 'bg-gradient-message-user text-white'
                  : 'bg-gradient-message-ai border border-nova-green/20 text-white'
                  } px-4 py-3 rounded-2xl overflow-hidden`}
              >
                {message.sender === 'ai' ? (
                  <div className="text-sm prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                )}

                {/* Show QR code if survey was created */}
                {message.sender === 'ai' && message.toolCalls && message.toolCalls.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {message.toolResults // This is now valid
                      .filter(tr => tr.content && (tr.content.survey_url && tr.content.title))
                      .map((tr, idx) => {

                        const surveyUrl = tr.content.survey_url;
                        const surveyTitle = tr.content.title;

                        if (surveyUrl && surveyTitle) {
                          return (
                            <div key={idx} className="bg-nova-bg/50 rounded-lg p-3 border border-nova-green/20">
                              <SurveyQRCode
                                surveyUrl={surveyUrl}
                                surveyTitle={surveyTitle}
                                size={200}
                              />
                            </div>
                          );
                        }
                        return null;
                      })
                    }
                  </div>
                )}

                <p className={`text-xs mt-2 ${message.sender === 'user' ? 'text-white/70' : 'text-gray-400'
                  }`}>
                  {message.timestamp}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-nova-secondary border border-nova-green/20 px-4 py-3 rounded-2xl flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-nova-green" />
                <span className="text-sm text-gray-400">Thinking...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Part of flex container, always visible */}
      <div className="flex-shrink-0 bg-gradient-to-t from-nova-bg via-nova-bg to-transparent pt-8" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        <div className="max-w-3xl mx-auto px-6">
          {/* Debug info */}
          {!sessionId && (
            <div className="text-xs text-nova-green mb-2">
              Waiting for session... (Input disabled)
            </div>
          )}

          <div>
            {/* Character count indicator */}
            {inputMessage.length > 0 && (
              <div className={`text-xs mb-2 text-right ${inputMessage.length > MAX_MESSAGE_LENGTH ? 'text-red-400' : 'text-gray-400'}`}>
                {inputMessage.length} / {MAX_MESSAGE_LENGTH} characters
              </div>
            )}

            <div className="flex items-end space-x-2 bg-nova-secondary rounded-3xl shadow-lg border border-nova-green/20 p-2">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                }}
                onKeyDown={handleKeyPress}
                placeholder="Type your message..."
                disabled={!sessionId}
                rows={1}
                className="flex-1 px-4 py-2 bg-transparent text-white focus:outline-none disabled:bg-transparent disabled:text-gray-500 placeholder-gray-500 resize-none overflow-y-auto max-h-40"
              />
              <button
                onMouseDown={preventBlur}
                onTouchStart={preventBlur}
                onClick={handleSendMessage}
                disabled={isLoading || !sessionId || !inputMessage.trim() || inputMessage.length > MAX_MESSAGE_LENGTH}
                className="w-10 h-10 bg-gradient-primary text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;